/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FileType } from 'src/core/types/FileType';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { getQnaMakerCommonService } from 'src/qnaMaker/core/services/qnaMakerCommonService';
import { getQnaMakerAssetsService } from 'src/qnaMaker/core/services/qnas/qnaMakerAssetsService';
import { QnaMakerQnaSwagger, QnaMakerSynonymSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaPatchOperation } from 'src/qnaMaker/core/types/qnaPatchOperation';

export type QnaMakerAssetsHandlerGroup = ReturnType<typeof createQnaMakerAssetsHandlerGroup>;

export const createQnaMakerAssetsHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    type AssetsType = 'qnas' | 'synonyms';

    const getQnaMakerAssets = async ({
        context,
        assetsType = 'qnas'
    }: HandlerParams<{
        assetsType?: AssetsType;
    }>) => {
        const { currentQnaMakerProjectStore } = rootStore;

        const swaggers = await getQnaMakerAssetsService({ context }).getAssets({
            projectId: currentQnaMakerProjectStore.id,
            assetsType
        });

        if (assetsType === 'synonyms') {
            swaggers.forEach((swagger, index) => {
                (swagger as QnaMakerSynonymSwagger).id = index + 1;
            });
            currentQnaMakerProjectStore.setSynonymsJsonData(swaggers as QnaMakerSynonymSwagger[]);
        } else {
            currentQnaMakerProjectStore.setQnasJsonData(swaggers as QnaMakerQnaSwagger[]);
        }
    };

    const saveQnaMakerAssets = async ({
        assetWithOperations,
        fetchUpdatedObjects = true,
        context,
        assetsType = 'qnas'
    }: HandlerParams<{
        assetWithOperations: QnaPatchOperation<QnaMakerQnaSwagger>[] | QnaMakerSynonymSwagger[];
        fetchUpdatedObjects?: boolean;
        assetsType?: AssetsType;
    }>) => {
        try {
            const { currentQnaMakerProjectStore } = rootStore;

            await getQnaMakerAssetsService({ context }).updateAssets({
                assetWithOperations,
                projectId: currentQnaMakerProjectStore.id,
                assetsType
            });
        } catch (e) {
            await getQnaMakerAssets({ context, assetsType });
            throw e;
        }

        if (fetchUpdatedObjects) {
            await getQnaMakerAssets({ context, assetsType });
        }
    };

    const importQnaMakerSynonyms = async ({
        file,
        fileType,
        context,
        assetsType = 'qnas'
    }: HandlerParams<{ file: File; fileType: Extract<FileType, 'xlsx' | 'tsv'>; assetsType?: AssetsType }>) => {
        const { currentQnaMakerProjectStore } = rootStore;

        let fileUri: string;
        try {
            fileUri = await getQnaMakerCommonService({ context }).uploadFiles({
                file
            });
        } catch (error) {
            // Error with MI upload, try SAS upload
            console.error('Failed to upload file using MI:', error);
            try {
                const sasUri = await getQnaMakerCommonService({ context }).getSASUri();

                fileUri = await getQnaMakerCommonService({ context }).uploadFilesWithKey({
                    file,
                    sasToken: sasUri.token,
                    sasEndpoint: sasUri.endpoint
                });
            } catch (keyError) {
                console.error('Failed to upload file using key or MI:', keyError);
                // Both upload methods failed
                throw keyError;
            }
        }

        await getQnaMakerAssetsService({ context }).importAssets({
            projectId: currentQnaMakerProjectStore.id,
            fileType,
            fileUri,
            assetsType
        });

        await getQnaMakerAssets({ context, assetsType });
    };

    const exportQnaMakerAssets = async ({
        fileType,
        context,
        assetsType = 'qnas'
    }: HandlerParams<{
        fileType: Extract<FileType, 'xlsx' | 'tsv'>;
        assetsType?: AssetsType;
    }>) => {
        const { currentQnaMakerProjectStore } = rootStore;

        await getQnaMakerAssetsService({ context }).exportAssets({
            projectId: currentQnaMakerProjectStore.id,
            fileType,
            assetsType
        });
    };

    const submitSmartSuggestionsFeedback = async ({
        context,
        isPositive,
        feedbackText
    }: HandlerParams<{
        isPositive: boolean;
        feedbackText: string;
    }>) => {
        await getQnaMakerAssetsService({ context }).submitSmartSuggestionsFeedback({
            isPositive,
            feedbackText
        });
    };

    return {
        getQnaMakerAssets,
        saveQnaMakerAssets,
        exportQnaMakerAssets,
        importQnaMakerSynonyms,
        submitSmartSuggestionsFeedback
    };
};
